// Números vivos da Central (mosaico de 12/09/2026): um número por porta e a
// frase do dia ("2 coisas precisam de você"). Só dado que já existe em API ou
// store barata: fila, tarefas, funil (reports do Kanban), campanhas e fluxos.
// Valor ausente vira null e o painel mostra "·", nunca zero falso.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import KanbanAPI from 'dashboard/api/kanban';
import FunnelAPI from 'dashboard/api/funnel';
import CampaignsAPI from 'dashboard/api/campaigns';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import {
  waitingDurationFromMinutes,
  waitingMinutesFromEpoch,
} from 'dashboard/helper/waitingLabel';
import { toIntlLocale } from 'shared/helpers/intlLocale';
import { dedupeInFlight } from 'dashboard/store/utils/dedupeInFlight';
import { isCampaignActive, isCampaignScheduled } from './useHubWorldStats';
import { useHubDataAccess } from './hubDataAccess';

import { ATTENTION_HEALTH } from 'dashboard/helper/workflowHealth';
export const EMPTY = '·';

// Soma do que esta em aberto em todos os funis, a partir do `stats` que
// funnels#index ja devolve. Exportada para o spec.
export const sumOpenPipeline = funnels =>
  (funnels || []).reduce(
    (acc, funnel) => ({
      items: acc.items + (Number(funnel?.stats?.open_items) || 0),
      value: acc.value + (Number(funnel?.stats?.open_value) || 0),
    }),
    { items: 0, value: 0 }
  );

// "Em aberto" e o que esta no funil HOJE (sem janela de data); "ganhos" sao
// os fechados nos ultimos 30 dias, pela data de fechamento. Antes o card pedia
// o relatorio sem periodo, que recorta por CRIACAO em 30 dias: conta com funil
// cheio de cartoes antigos aparecia com R$ 0 e "nenhuma negociacao em aberto".
// Hub montado de novo enquanto a primeira leitura corre divide a mesma
// promessa: um GET de funis e um de relatorio por carga, nao dois.
// Sem `withRevenue` (agente sem report_manage) o relatorio nem e pedido: a API
// devolvia 401 e o "ganhos" some do card em vez de virar "·".
export const loadHubKanbanNumbers = (accountId, { withRevenue = true } = {}) =>
  dedupeInFlight(
    `hub/kanban/${accountId ?? ''}/${withRevenue ? 'revenue' : 'open'}`,
    async () => {
      const to = Math.floor(Date.now() / 1000);
      const [funnels, reports] = await Promise.all([
        FunnelAPI.get(),
        withRevenue
          ? KanbanAPI.getReports({ from: to - 30 * 86400, to })
          : null,
      ]);
      const list = Array.isArray(funnels?.data)
        ? funnels.data
        : funnels?.data?.payload || [];
      const open = sumOpenPipeline(list);
      return {
        items: open.items,
        value: open.value,
        revenue: withRevenue
          ? Number(reports?.data?.closedInPeriod?.revenue) || 0
          : null,
      };
    }
  );

// A Central V2 monta dentro do HubView: o HubView roda o useHubLiveMeta (o
// rodapé vivo) e a Central roda este composable, os dois no mesmo ciclo de
// montagem. Cada um buscava campanhas, fluxos, tarefas e o meta das conversas
// por conta própria, e cada GET saía duas vezes. As leituras abaixo são as
// mesmas para os dois e dividem a promessa em voo (por conta): uma request
// por montagem. A carga seguinte lê de novo.
const hubKey = (name, accountId) => `hub/${name}/${accountId ?? ''}`;

export const loadHubCampaigns = accountId =>
  dedupeInFlight(hubKey('campaigns', accountId), async () => {
    const { data } = await CampaignsAPI.get();
    return Array.isArray(data) ? data : [];
  });

export const loadHubFlows = accountId =>
  dedupeInFlight(hubKey('flows', accountId), async () => {
    const { data } = await conversationWorkflowsAPI.get();
    return data?.payload ?? [];
  });

export const loadHubTaskNotifications = store => {
  const key = hubKey('tasks', store.getters.getCurrentAccountId);
  return dedupeInFlight(key, () => store.dispatch('tasks/fetchNotifications'));
};

// Mesmo recorte do chip "Minhas" da lista (aberto+pendente+adiado); sem o
// status o backend conta só abertas e o badge diverge do chip.
export const refreshHubConversationStats = store => {
  const key = hubKey('conversationStats', store.getters.getCurrentAccountId);
  return dedupeInFlight(key, () =>
    store.dispatch('conversationStats/get', {
      assigneeType: 'me',
      status: 'active',
    })
  );
};

export function useHubOverview() {
  const { t, locale } = useI18n();
  const store = useStore();
  const conversationStats = useMapGetter('conversationStats/getStats');
  const allConversations = useMapGetter('getAllConversations');
  const access = useHubDataAccess();

  const kanban = ref({ items: null, value: null, revenue: null });
  const campaigns = ref({ active: null, scheduled: null });
  const flows = ref({ active: null, attention: null, attentionName: '' });

  const intl = toIntlLocale(locale.value);
  const compactMoney = new Intl.NumberFormat(intl, {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  });

  const mineCount = computed(() => conversationStats.value?.mineCount || 0);
  const unassignedCount = computed(
    () => conversationStats.value?.unAssignedCount || 0
  );
  const allCount = computed(() => conversationStats.value?.allCount || 0);
  const longestWaiting = computed(() => {
    const waiting = (allConversations.value || []).filter(
      chat => Number(chat.waiting_since) > 0
    );
    if (!waiting.length) return null;
    const chat = waiting.reduce((oldest, current) =>
      Number(current.waiting_since) < Number(oldest.waiting_since)
        ? current
        : oldest
    );
    return {
      minutes: Math.max(0, waitingMinutesFromEpoch(chat.waiting_since)),
      name: chat.meta?.sender?.name || '',
    };
  });
  const tasksToday = computed(() => store.getters['tasks/getTodayCount'] ?? 0);
  const tasksOverdue = computed(
    () => store.getters['tasks/getOverdueCount'] ?? 0
  );

  const money = value => (value === null ? EMPTY : compactMoney.format(value));

  const loadKanban = async () => {
    try {
      kanban.value = await loadHubKanbanNumbers(
        store.getters.getCurrentAccountId,
        { withRevenue: access.value.kanbanReports }
      );
    } catch {
      kanban.value = { items: null, value: null, revenue: null };
    }
  };

  const loadCampaigns = async () => {
    try {
      const records = await loadHubCampaigns(store.getters.getCurrentAccountId);
      campaigns.value = {
        active: records.filter(isCampaignActive).length,
        scheduled: records.filter(isCampaignScheduled).length,
      };
    } catch {
      campaigns.value = { active: null, scheduled: null };
    }
  };

  const loadFlows = async () => {
    try {
      const list = await loadHubFlows(store.getters.getCurrentAccountId);
      const attention = list.filter(flow => ATTENTION_HEALTH.has(flow.health));
      flows.value = {
        active: list.filter(flow => flow.active).length,
        attention: attention.length,
        attentionName: attention[0]?.name || '',
      };
    } catch {
      flows.value = { active: null, attention: null, attentionName: '' };
    }
  };

  onMounted(() => {
    refreshHubConversationStats(store).catch(() => {});
    loadHubTaskNotifications(store).catch(() => {});
    loadKanban();
    // Sem permissão o número fica null e o card troca o número pela descrição
    // da área: nada de GET que volta 401.
    if (access.value.campaigns) loadCampaigns();
    if (access.value.flows) loadFlows();
  });

  // O que precisa de você, na ordem em que dói: fila sem ninguém, tarefa
  // atrasada, fluxo parado.
  const needs = computed(() => {
    const items = [];
    if (unassignedCount.value > 0) {
      items.push(
        t('HUB.CENTRAL.NEED_UNASSIGNED', unassignedCount.value, {
          count: unassignedCount.value,
        })
      );
    }
    if (tasksOverdue.value > 0) {
      items.push(
        t('HUB.CENTRAL.NEED_OVERDUE', tasksOverdue.value, {
          count: tasksOverdue.value,
        })
      );
    }
    if (flows.value.attention > 0) {
      items.push(
        t('HUB.CENTRAL.NEED_FLOWS', flows.value.attention, {
          count: flows.value.attention,
        })
      );
    }
    return items;
  });

  const needsSentence = computed(() => {
    if (!needs.value.length) return t('HUB.CENTRAL.ALL_CLEAR');
    const list =
      needs.value.length > 1
        ? `${needs.value.slice(0, -1).join(', ')} ${t('HUB.CENTRAL.AND')} ${needs.value.at(-1)}`
        : needs.value[0];
    // Plural com campo nomeado: vue-i18n só interpola `list` na forma
    // t(chave, nomeados, plural).
    return t(
      'HUB.CENTRAL.TODAY_NEEDS',
      { count: needs.value.length, list },
      needs.value.length
    );
  });

  const maxWaitLabel = computed(() =>
    longestWaiting.value
      ? waitingDurationFromMinutes(longestWaiting.value.minutes, t)
      : ''
  );

  return {
    mineCount,
    unassignedCount,
    allCount,
    longestWaiting,
    maxWaitLabel,
    tasksToday,
    tasksOverdue,
    access,
    kanban,
    campaigns,
    flows,
    money,
    needs,
    needsSentence,
  };
}
